export const PERCENT_KG_MS = "percent_kg_ms";
export const KG_MS_PER_BASE_KG_MS = "kg_ms_per_base_kg_ms";
export const KG_MO = "kg_mo";

const BASE_DRY_MATTER = 100;

export function isOriginalMatterFormulation(formulateItems) {
  return (formulateItems || []).some((item) => item.amountUnit === KG_MO);
}

export function getAddedDryMatter(item) {
  const added = Number(item.addedDryMatterAmount) || 0;
  const base = Number(item.baseDryMatterAmount) || 0;
  if (added <= 0 || base <= 0) {
    return 0;
  }
  return (added / base) * BASE_DRY_MATTER;
}

export function getTotalDryMatter(formulateItems) {
  const items = formulateItems || [];
  const added = items
    .filter((item) => item.amountUnit === KG_MS_PER_BASE_KG_MS)
    .reduce((total, item) => total + getAddedDryMatter(item), 0);
  return BASE_DRY_MATTER + added;
}

export function getTotalPercentAmount(formulateItems) {
  return (formulateItems || [])
    .filter((item) => item.amountUnit !== KG_MS_PER_BASE_KG_MS && item.amountUnit !== KG_MO)
    .reduce((total, item) => total + (Number(item.amount) || 0), 0);
}

export function computeDryMatterFractions(formulateItems) {
  const items = formulateItems || [];

  if (isOriginalMatterFormulation(items)) {
    const totalDryMatter = items.reduce(
      (total, item) =>
        total +
        (Number(item.originalMatterAmount) || 0) *
          ((item.supplementOrRawMaterial?.dryMatterPercentage || 0) / 100),
      0,
    );
    return items.map((item) => {
      if (totalDryMatter <= 0) {
        return 0;
      }
      const dryMatter =
        (Number(item.originalMatterAmount) || 0) *
        ((item.supplementOrRawMaterial?.dryMatterPercentage || 0) / 100);
      return dryMatter / totalDryMatter;
    });
  }

  const totalDryMatter = getTotalDryMatter(items);

  return items.map((item) => {
    if (totalDryMatter <= 0) {
      return 0;
    }
    const dryMatter =
      item.amountUnit === KG_MS_PER_BASE_KG_MS
        ? getAddedDryMatter(item)
        : Number(item.amount) || 0;
    return dryMatter / totalDryMatter;
  });
}

export const FORMULATION_NUTRIENTS = [
  "cp",
  "tdn",
  "nitrogen",
  "phosphorus",
  "zinc",
  "sodium",
  "calcium",
  "sulfur",
  "potassium",
];

const GRAMS_PER_KG = 1000;

export function convertNutrientToGramsPerKg(fraction, nutrient, nutrientUnit) {
  const value = Number(nutrient) || 0;
  switch (nutrientUnit) {
    case "g_kg":
      return fraction * value;
    case "mg_kg":
      return (fraction * value) / GRAMS_PER_KG;
    case "percentage":
    default:
      return (fraction * GRAMS_PER_KG * value) / 100;
  }
}

export function isFormulateItemValid(item, isOriginalMatter) {
  if (!item.supplementOrRawMaterial) {
    return false;
  }
  if (isOriginalMatter) {
    return item.originalMatterAmount > 0;
  }
  if (item.amountUnit === KG_MS_PER_BASE_KG_MS) {
    return item.addedDryMatterAmount > 0 && item.baseDryMatterAmount > 0;
  }
  return item.amount > 0;
}

export function computeFormulationNutrients(formulateItems) {
  const items = formulateItems || [];
  const isOriginalMatter = isOriginalMatterFormulation(items);
  const fractions = computeDryMatterFractions(items);
  const result = FORMULATION_NUTRIENTS.reduce(
    (acc, nutrient) => ({ ...acc, [nutrient]: 0 }),
    { cost: 0, dryMatterPercentage: 0 },
  );
  let originalMatterPerDryMatterKg = 0;

  items.forEach((item, index) => {
    if (!isFormulateItemValid(item, isOriginalMatter)) {
      return;
    }
    const fraction = fractions[index] || 0;
    const rawMaterial = item.supplementOrRawMaterial;
    FORMULATION_NUTRIENTS.forEach((nutrient) => {
      result[nutrient] += convertNutrientToGramsPerKg(
        fraction,
        rawMaterial[nutrient],
        rawMaterial[`${nutrient}Unit`],
      );
    });
    const dryMatter = (Number(rawMaterial.dryMatterPercentage) || 0) / 100;
    if (dryMatter > 0) {
      const originalMatter = fraction / dryMatter;
      originalMatterPerDryMatterKg += originalMatter;
      result.cost += originalMatter * (Number(item.cost) || 0);
    }
  });

  result.dryMatterPercentage =
    originalMatterPerDryMatterKg > 0 ? 100 / originalMatterPerDryMatterKg : 0;
  return result;
}
